import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosHeaders
} from 'axios';
import { API_CONFIG } from '../config/api.config';

// Default headers
const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

// Create an Axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  headers: defaultHeaders,
  withCredentials: true // Enable sending cookies in cross-origin requests
});

// Add request debugging
apiClient.interceptors.request.use(
  (config) => {
    // Ensure headers exist and are properly typed
    config.headers = config.headers || new AxiosHeaders();

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response debugging
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      // Handle authentication errors (401)
      if (error.response?.status === 401) {
        // Check if this is a refresh token request to avoid infinite loops
        const isRefreshRequest = error.config?.url?.includes('/auth/refresh');

        if (isRefreshRequest) {
          // If the refresh request itself fails, log out completely
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Don't redirect if already on login page
          if (!window.location.href.includes('/login')) {
            window.location.href = '/login';
          }
        } else {
          // For non-refresh requests that get 401, try to refresh the token
          try {
            // Import dynamically to avoid circular dependencies
            const authServiceModule = await import('./authService');
            const success = await authServiceModule.authService.refreshToken();

            if (success && error.config) {
              // If token refresh succeeded, retry the original request
              const token = localStorage.getItem('token');
              // Update the token in the retry request
              if (token && error.config.headers) {
                error.config.headers.Authorization = `Bearer ${token}`;
              }
              return await apiClient(error.config);
            } else {
              // If refresh failed, clear token and redirect to login
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              // Don't redirect if already on login page
              if (!window.location.href.includes('/login')) {
                window.location.href = '/login';
              }
            }
          } catch (refreshError) {
            // Clear token and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Don't redirect if already on login page
            if (!window.location.href.includes('/login')) {
              window.location.href = '/login';
            }
          }
        }
      }
    } else {
      console.error('Network error or request cancelled:', error.message);
    }
    return Promise.reject(error);
  }
);

// API service with methods to handle HTTP requests
// Export individual functions instead of an object
const get = <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return apiClient
    .get<T, AxiosResponse<T>>(url, config)
    .then((response) => response.data);
};

const post = <T, D = Record<string, unknown>>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient
    .post<T, AxiosResponse<T>>(url, data, config)
    .then((response) => response.data);
};

const put = <T, D = Record<string, unknown>>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient
    .put<T, AxiosResponse<T>>(url, data, config)
    .then((response) => response.data);
};

const deleteRequest = <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient
    .delete<T, AxiosResponse<T>>(url, config)
    .then((response) => response.data);
};

// Export individual functions
export { get, post, put, deleteRequest as delete };

// For backward compatibility
export default {
  get,
  post,
  put,
  delete: deleteRequest
};
